import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { execute } from '@/graphql/execute';
import { useOrganizations } from '@/hooks/useOrganizations';
import { useDialog } from '@/providers/DialogProvider';
import { deleteOrganizationMutation } from '@/queries/organization.query';
import { useFormik } from 'formik';
import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

export const DeleteOrganizationDialog: FunctionComponent<{ organizationId: string; organizationName: string }> = ({
  organizationId,
  organizationName,
}) => {
  const { dismissActiveDialog } = useDialog();
  const { refreshOrganizations } = useOrganizations();
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      password: '',
      confirmText: '',
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (values.confirmText !== organizationName) {
        errors.confirmText = 'Organization name does not match';
      }
      return errors;
    },
    onSubmit: async (_values) => {
      const t = toast.loading('Deleting organization...');
      try {
        await execute(deleteOrganizationMutation, { id: organizationId, password: _values.password });
        toast.success('Organization deleted', { id: t });
        dismissActiveDialog();
        navigate('/', { replace: true });
        await refreshOrganizations();
      } catch (error) {
        toast.error(`Failed to delete organization: ${error instanceof Error ? error.message : 'Unknown error'}`, {
          id: t,
        });
      }
    },
  });

  return (
    <div className='space-y-4'>
      <div className='flex flex-col gap-0.5'>
        <h3 className='font-semibold text-destructive'>Delete Organization</h3>
        <p className='text-sm leading-none opacity-70'>
          This action cannot be undone. This will permanently delete the organization{' '}
          <span className='font-semibold'>{organizationName}</span> and all its projects, data, and files.
        </p>
      </div>
      <form onSubmit={formik.handleSubmit} className='space-y-4'>
        <div className='space-y-2'>
          <Label htmlFor='password'>Current Password</Label>
          <Input
            id='password'
            name='password'
            type='password'
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder='Enter your current password'
          />
        </div>
        <div className='space-y-2'>
          <Label htmlFor='confirmText'>
            Type <span className='font-semibold'>{organizationName}</span> to confirm
          </Label>
          <Input
            id='confirmText'
            name='confirmText'
            value={formik.values.confirmText}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder={organizationName}
          />
          {formik.touched.confirmText && formik.errors.confirmText && (
            <p className='text-sm text-red-600'>{formik.errors.confirmText}</p>
          )}
        </div>
        <div className='flex justify-end space-x-2'>
          <Button type='button' variant='outline' onClick={dismissActiveDialog}>
            Cancel
          </Button>
          <Button type='submit' variant='destructive' disabled={formik.isSubmitting}>
            Delete Organization
          </Button>
        </div>
      </form>
    </div>
  );
};
